import "./home.css";
import { Helmet } from "react-helmet-async";
import ScrollUp from "../../components/scrollUp/scrollUp";
import {
  Component1,
  Component2,
  Component3,
  Component5,
  Component6,
  Component7,
  Component8,
} from "./Components/Components";
import { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

// Time config
let today = new Date();
let twoDaysLater = new Date(today);
twoDaysLater.setDate(today.getDate() + 2);

const Home = ({ Data, SetData }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [categories, setCategories] = useState([]);

  const PAGE_SIZE = 24;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('category')
          .eq('is_active', true)
          .order('category', { ascending: true });

        if (error) throw error;

        const cats = [...new Set((data || []).map(r => r.category).filter(Boolean))];
        setCategories(cats);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      // Only show full-page loading on initial load (page 0)
      if (page === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      try {
        let query = supabase
          .from('products')
          .select(`
            *,
            variants (*)
          `, { count: 'exact' })
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (selectedCategory) {
          query = query.eq('category', selectedCategory);
        }

        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data, error, count } = await query.range(from, to);

        if (error) throw error;

        const mapped = (data || []).map(p => ({
          id: p.id,
          title: p.title,
          description: p.description || '',
          price: p.price_retail,
          priceWholesale: p.price_wholesale,
          wholesaleMinQty: p.wholesale_min_qty || 1,
          oldPrice: Math.round(p.price_retail / 0.51),
          discount: "-49%",
          img: p.images && p.images.length > 0 ? p.images[0] : "https://via.placeholder.com/300?text=No+Image",
          images: p.images || [],
          variants: p.variants || [],
          reviewStar: 4.5,
          numperReview: 12,
          cardActive: "",
          isWishlisted: false,
          category: p.category || "Uncategorized"
        }));

        // Append new products to existing ones, or replace if page 0
        setProducts((prev) => (page === 0 ? mapped : [...prev, ...mapped]));
        setHasMore(typeof count === 'number' ? (to + 1 < count) : mapped.length === PAGE_SIZE);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedCategory]);

  const HomeData = Data.Home;

  // Handler for adding to cart
  const handleAddToCart = (product, mode = 'retail', selectedVariant = null) => {
    // 1. Visual Feedback: Update local product state to show "Added" (bag-check-fill)
    setProducts(prev => prev.map(p =>
      p.id === product.id ? { ...p, cardActive: "active" } : p
    ));

    // 2. Global State: Add to Cart if not already present
    const existingCart = Data.Cart || [];
    const variantId = selectedVariant?.id || null;
    // Check if this product-mode-variant combination already exists
    const itemExists = existingCart.some(item => item.id === product.id && item.mode === mode && (item.variantId || null) === variantId);

    if (!itemExists) {
      const basePrice = mode === 'wholesale' ? (product.priceWholesale || product.price) : product.price;
      const variantPrice = (() => {
        if (!selectedVariant) return null;
        const raw = mode === 'wholesale' ? selectedVariant.price_wholesale : selectedVariant.price_retail;
        if (raw === null || raw === undefined || raw === '') return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })();
      const resolvedPrice = variantPrice !== null ? variantPrice : basePrice;

      const newCartItem = {
        productPosition: "DynamicProduct",
        productImg: product.img,
        productName: product.title,
        productPrice: resolvedPrice,
        productQuantity: mode === 'wholesale' ? (product.wholesaleMinQty || 1) : 1,
        id: product.id,
        mode: mode,
        wholesaleMinQty: product.wholesaleMinQty || 1, // Store for validation
        variantId: variantId,
        variantName: selectedVariant ? `${selectedVariant.name}: ${selectedVariant.value}` : null,
        variantPrice: variantPrice
      };
      // Use SetData from App.js to update global state
      SetData(prev => ({ ...prev, Cart: [...existingCart, newCartItem] }));
    }
  };

  // Handler for toggling wishlist
  const handleToggleWishlist = (product) => {
    setProducts(prev => prev.map(p =>
      p.id === product.id ? { ...p, isWishlisted: !p.isWishlisted } : p
    ));
    // Future: Save to localStorage or Supabase
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="spinner-border" style={{ color: "var(--mix_red)" }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const allCategoryName = 'All';
  
  // Get first product image for each category
  const getCategoryImage = (categoryName) => {
    if (categoryName === allCategoryName) return null;
    const product = products.find(p => p.category === categoryName && p.images && p.images.length > 0);
    return product?.images?.[0] || null;
  };

  const uniqueCategories = [allCategoryName, ...categories].map((cat, index) => ({
    categoryId: `cat-${index}`,
    categoryName: cat,
    categoryIcon: "apps-outline",
    categoryImage: getCategoryImage(cat),
    categoryLink: "#"
  }));

  const handleCategorySelect = (categoryName) => {
    const normalized = String(categoryName || '').trim();
    setSelectedCategory(!normalized || normalized === allCategoryName ? null : normalized);
    setPage(0);
    setShowAllProducts(true);
  };

  const handleViewAllProducts = () => {
    setShowAllProducts(true);
    // Don't scroll, just show all products from current position
  };

  return (
    <>
      <Helmet>
        <title>ThinkTwiceOnline Store | Home</title>
        <meta name="description" content="ThinkTwiceOnline Store | ecommerce website" />
      </Helmet>

      <Component1
        Data={HomeData.Component1}
        Categories={uniqueCategories}
        onCategorySelect={handleCategorySelect}
      />

      <Component2
        TargetDate={twoDaysLater}
        Data={products.length > 0 ? products.slice(0, 12) : HomeData.Component2}
        addToCart={handleAddToCart}
        toggleWishlist={handleToggleWishlist}
        rawData={Data}
        setrawData={SetData}
      />

      <Component3 Data={uniqueCategories} onCategorySelect={handleCategorySelect} />

      <Component5 TargetDate={twoDaysLater} Data={HomeData.Component5} />
      
      <Component6
        Data={products.length > 0 ? (showAllProducts ? products : products.slice(0, 8)) : HomeData.Component6}
        addToCart={handleAddToCart}
        toggleWishlist={handleToggleWishlist}
        rawData={Data}
        setrawData={SetData}
        onViewAll={!showAllProducts ? handleViewAllProducts : null}
      />

      {showAllProducts && hasMore && (
        <div className="d-flex justify-content-center my-4">
          <button
            className="btn btn-outline-danger btn-load-more"
            disabled={loadingMore}
            onClick={() => setPage((p) => p + 1)}
          >
            {loadingMore ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading...
              </>
            ) : (
              'Load More Products'
            )}
          </button>
        </div>
      )}
      <Component7 Data={HomeData.Component7} />
      <Component8 Data={HomeData.Component8} />
      <ScrollUp />
    </>
  );
};

export default Home;
